// Tells the caller when the reader's own conversation about an issue has appeared between one
// snapshot and the next, and returns that issue's id on the render that first sees it.
//
// Mor opens an issue from the topic conversation, and the screen follows into its workspace the
// way it follows into a started topic. The first snapshot is only remembered, so a reload onto
// a plan that already has issue conversations reports nothing, and so does the first snapshot
// after a change of plan.

import { useEffect, useRef } from 'react';

import type { PlanSnapshot } from './snapshot.js';

function issueIds(snapshot: PlanSnapshot): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const thread of snapshot.threads) {
    if (thread.issue !== null) {
      ids.add(thread.issue);
    }
  }
  return ids;
}

export function useStartedIssue(
  snapshot: PlanSnapshot | null,
  onStarted: (issueId: string, topic: string | null) => void,
): void {
  const previous = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (snapshot === null) {
      previous.current = null;
      return;
    }
    const before = previous.current;
    const now = issueIds(snapshot);
    previous.current = now;
    if (before === null) {
      return;
    }
    const thread = snapshot.threads.find(
      (candidate) => candidate.issue !== null && !before.has(candidate.issue),
    );
    if (thread !== undefined && thread.issue !== null) {
      onStarted(thread.issue, thread.topic);
    }
    // `onStarted` is read when the snapshot changes; only the snapshot drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);
}
